import React from 'react';

// Styles & Assets
import stylesColumns from '../../../styles/Columns.module.css';

// Hooks
import { useTranslation } from 'react-i18next';
import useTranslate from '../../../hooks/useTranslate';

// Components
import TableConstants from '../TableConstants/TableConstants';
import SelectFrequency from '../../Helpers/SelectFrequency/SelectFrequency';
import HourSelector from '../../UI/HourSelector/HourSelector';
import AutocompletePreconfig from '../../Helpers/AutocompletePreconfig/AutocompletePreconfig';

// Constants
import { DEFAULT_SHEET_PARAMETER_DATA } from '../../../data/ConstantsPreconfig';

// Interfaces
import { Column } from '../../../interfaces/Columns';
import { PreconfigSheetParameter } from '../../../interfaces/Preconfigurations';
import { HH, mm } from '../../../interfaces/Time';

// Other
import { PreconfigDispatch } from '../../../reducers/preconfig-reducer';
import { useFormPreconfig } from '../../../hooks/preconfig-hooks';
import { Parameter } from '../../../interfaces/Profile';
interface Props {
    sheetParameters: Parameter[]
    preconfigSheetParameters: PreconfigSheetParameter[]
    constantRowErrors?: { [key: number]: number[] }
    dispatchPreconfigSheetParams: React.Dispatch<PreconfigDispatch>
}

const PreconfigConstantsTable: React.FC<Props> = ({
    sheetParameters,
    preconfigSheetParameters,
    constantRowErrors,
    dispatchPreconfigSheetParams
}) => {

    const { t } = useTranslation();
    const { translateMiddleware } = useTranslate();

    const {
        handleChangeConstant,
        handleChangeFreq,
        handleChangeValue,
        handleChangeReminder,
    } = useFormPreconfig(sheetParameters, dispatchPreconfigSheetParams);

    const styleSelects = { height: '35px', outline: 'none', padding: '8px 16px 8px 6px', fontSize: '0.8rem' };

    const options = sheetParameters.map(param => ({
        id: param.id,
        label: translateMiddleware(param.name_translations)?.text ?? param.name ?? param.id ?? ''
    }))

    const COLUMNS: Column<PreconfigSheetParameter>[] = [
        {
            id: 0,
            name: () => t('vital_signs'),
            visible: true,
            sortable: false,
            element: constant =>
                <AutocompletePreconfig
                    options={options}
                    value={options.find(o => o.id === constant.sheet_parameter_id)}
                    placeholder={t('select_vital_sign') as string}
                    getOptionDisabled={(o) => preconfigSheetParameters.some(p => p.sheet_parameter_id === o.id)}
                    handleChangeConstant={handleChangeConstant(constant.sheet_parameter_id ?? null)}
                />
        },
        {
            id: 1,
            name: () => t('minimum_critical_value'),
            visible: true,
            sortable: false,
            element: constant =>
                <input
                    className={stylesColumns.inputText}
                    type='number'
                    value={constant.min_limit ?? ''}
                    onChange={handleChangeValue(constant.sheet_parameter_id ?? '', 'CHANGE_MIN_LIMIT')}
                />
        },
        {
            id: 2,
            name: () => t('maximum_critical_value'),
            visible: true,
            sortable: false,
            element: constant =>
                <input
                    className={stylesColumns.inputText}
                    type='number'
                    value={constant.max_limit ?? ''}
                    onChange={handleChangeValue(constant.sheet_parameter_id ?? '', 'CHANGE_MAX_LIMIT')}
                />
        },
        {
            id: 3,
            name: () => t('frequency'),
            visible: true,
            sortable: false,
            element: constant =>
                <SelectFrequency
                    pollFrequency={{
                        frecuency: constant.frecuency,
                        monthly_frequency_day: constant.monthly_frequency_day,
                        semanal_frequency: constant.semanal_frequency,
                        frequency_duration: constant.frequency_duration
                    }}
                    handleChangeFreq={handleChangeFreq(constant.sheet_parameter_id ?? '')}
                    style={styleSelects}
                />
        },
        {
            id: 4,
            name: () => t('reminder'),
            visible: true,
            sortable: false,
            element: constant =>
                <HourSelector
                    hours={constant.reminder_time?.slice(0, 2) as HH || null}
                    minutes={constant.reminder_time?.slice(3, 5) as mm || null}
                    handleChange={handleChangeReminder(constant.sheet_parameter_id ?? '')}
                    style={{ border: 'none' }}
                />,
            classBody: stylesColumns.reminderCell
        },
    ];

    const rowsWithExtra = [...preconfigSheetParameters.map((p, i) => ({ ...p, id: i })), DEFAULT_SHEET_PARAMETER_DATA];


    return (
        <TableConstants
            rows={rowsWithExtra}
            columns={COLUMNS}
            errorCells={constantRowErrors}
        />
    );
};

export default PreconfigConstantsTable;